import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import Loading from '../../../components/loading';
import ConsultationTypeFormModal from '../consultationTypeFormModal';
import { useAppointmentTypes } from '../../../hooks/useAppointmentTypes';
import { useDeleteConsultationType } from '../hooks/useDeleteConsultationType';

function ConsultationTypeListWeb() {
  const navigate = useNavigate();

  const {
    appointmentTypes: consultationTypes,
    isLoading,
    error: listError,
    refetch: getConsultationTypes,
  } = useAppointmentTypes();
  const { remove } = useDeleteConsultationType();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedConsultationType, setSelectedConsultationType] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedConsultationTypeId, setSelectedConsultationTypeId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const clickMenu = (event, consultationTypeId) => {
    setAnchorEl(event.currentTarget);
    setSelectedConsultationTypeId(consultationTypeId);
  };

  const closeMenu = () => setAnchorEl(null);

  const openCreateModal = () => {
    setSelectedConsultationType(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    const type = consultationTypes.find((t) => t.id === selectedConsultationTypeId);
    if (type) {
      setSelectedConsultationType(type);
      setModalOpen(true);
    }
    closeMenu();
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedConsultationType(null);
  };

  const openConfirmModal = () => {
    setAnchorEl(null);
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedConsultationTypeId(null);
  };

  const handleConsultationTypeSaved = () => {
    getConsultationTypes();
    setSuccessMessage('Tipo de consulta salvo com sucesso.');
    setShowSuccessMessage(true);
  };

  const confirmDelete = () => {
    setConfirmModalOpen(false);
    remove(selectedConsultationTypeId)
      .then((res) => {
        getConsultationTypes();
        setSuccessMessage(res.data ?? 'Tipo de consulta excluído com sucesso.');
        setShowSuccessMessage(true);
      })
      .catch((err) => {
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? 'Erro ao excluir tipo de consulta.');
        if (err.response?.status === 401) navigate('/expired-session');
      })
      .finally(() => {
        setSelectedConsultationTypeId(null);
      });
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ p: 4 }}>
        <Container maxWidth="lg">
          <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: '12px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h5" component="h1" sx={{ fontWeight: '600' }}>
                Tipos de Consulta
              </Typography>
              <Button
                variant="outlined"
                onClick={openCreateModal}
                data-cy="add-consultation-type-btn"
              >
                Adicionar Tipo de Consulta
              </Button>
            </Box>

            <List>
              {consultationTypes.length === 0 && !isLoading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  Nenhum tipo de consulta cadastrado
                </Typography>
              ) : (
                consultationTypes.map((consultationType) => (
                  <React.Fragment key={consultationType.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton onClick={(event) => clickMenu(event, consultationType.id)}>
                          <MoreVertIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={consultationType.description} />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>
        </Container>

        <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
          <MenuItem onClick={openEditModal}>Editar</MenuItem>
          <MenuItem onClick={openConfirmModal} sx={{ color: 'error.main' }}>
            Excluir
          </MenuItem>
        </Menu>

        <ConsultationTypeFormModal
          open={isModalOpen}
          onClose={closeModal}
          onSave={handleConsultationTypeSaved}
          consultationType={selectedConsultationType}
        />

        <Dialog open={isConfirmModalOpen} onClose={closeConfirmModal}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Você tem certeza que deseja excluir este tipo de consulta? Essa ação não pode ser
              desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirmModal}>Cancelar</Button>
            <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default ConsultationTypeListWeb;
